import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useCallback, useEffect, useState } from 'react';
import { getCollection, requestJson } from '../lib/api';
import { DEFAULT_MENU_CONFIG, MENU_ICON_OPTIONS, normalizeMenuConfig } from '../lib/menuConfig';
import { useMenuPage } from '../hooks/useMenuPage';

function uniqueList(values) {
  const source = Array.isArray(values) ? values : String(values || '').split('\n');
  return [...new Set(
    source
      .map((item) => String(item || '').trim())
      .filter((item) => item && !hiddenMountingTypes.has(item))
  )];
}

function normalizeCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `item_${Date.now()}`;
}

const defaultSmtpSettings = {
  enabled: false,
  host: '',
  port: 587,
  secure: false,
  user: '',
  password: '',
  hasPassword: false,
  fromName: 'Optibox',
  fromEmail: '',
  replyTo: '',
  profiles: {
    logistics: {
      inheritGlobal: true,
      fromName: 'Optibox Logistique',
      fromEmail: '',
      replyTo: '',
      signature: '',
      signatureName: '',
      signatureRole: '',
      signaturePhone: '',
      signatureMobile: '',
      signatureAddress: '',
    },
    workshop: {
      inheritGlobal: true,
      fromName: 'Optibox Atelier',
      fromEmail: '',
      replyTo: '',
      signature: '',
      signatureName: '',
      signatureRole: '',
      signaturePhone: '',
      signatureMobile: '',
      signatureAddress: '',
    },
  },
};

const hiddenMountingTypes = new Set(['N.C.A']);
const defaultMountingTypes = ['T.D.', 'Classique', 'Easy-Digifit', 'Percées', 'V.S.', '+Gravure', 'Clip', 'Traçabilité'];

const defaultNonConformityScopes = [
  { value: 'right_lens', label: 'Verre droit' },
  { value: 'left_lens', label: 'Verre gauche' },
  { value: 'frame', label: 'Monture' },
  { value: 'other', label: 'Autres' },
];

const defaultNonConformityReasons = [
  { value: 'scratch', label: 'Rayure(s)', scopes: ['right_lens', 'left_lens'] },
  { value: 'chip', label: 'Éclat(s)', scopes: ['right_lens', 'left_lens'] },
  { value: 'defect', label: 'Défaut(s)', scopes: ['right_lens', 'left_lens', 'frame', 'other'] },
  { value: 'mounting', label: 'Montage', scopes: ['right_lens', 'left_lens', 'frame'] },
  { value: 'other', label: 'Autres', scopes: ['right_lens', 'left_lens', 'frame', 'other'] },
];

function normalizeNonConformityScopes(values) {
  const source = Array.isArray(values) ? values : defaultNonConformityScopes;
  return source
    .map((item) => ({
      value: String(item?.value || normalizeCode(item?.label)).trim(),
      label: String(item?.label || item?.value || '').trim(),
    }))
    .filter((item, index, list) =>
      item.value &&
      item.label &&
      list.findIndex((candidate) => candidate.value === item.value) === index
    );
}

function normalizeNonConformityReasons(values, scopes = defaultNonConformityScopes) {
  const allowedScopes = new Set(scopes.map((item) => item.value));
  const source = Array.isArray(values) ? values : defaultNonConformityReasons;
  return source
    .map((item) => ({
      value: String(item?.value || normalizeCode(item?.label)).trim(),
      label: String(item?.label || item?.value || '').trim(),
      scopes: [...new Set((Array.isArray(item?.scopes) ? item.scopes : [])
        .map((scope) => String(scope || '').trim())
        .filter((scope) => allowedScopes.has(scope)))],
    }))
    .filter((item, index, list) =>
      item.value &&
      item.label &&
      item.scopes.length &&
      list.findIndex((candidate) => candidate.value === item.value) === index
    );
}

const defaultSavSettings = {
  defects: [
    'RAYURE',
    'CASSE',
    'DEFORMATION',
    'BRULURE',
    'TRAITEMENT',
    'INVERSION VERRE',
    'COMMANDE',
    'ECLAT',
    'MAUVAIS CHOIX FINITION',
    'MONTURE ABIME',
    'ECARTS DE COTE',
    'ERREUR CENTRAGE HORIZONTAL',
    'ERREUR CENTRAGE VERTICAL',
    'ERREUR AXE',
  ],
  replacementParts: [
    '1 MONTURE COMPLETE',
    '1 BRANCHE DROITE',
    '1 BRANCHE GAUCHE',
    '1 FACE',
    '1 PAIRE DE MANCHONS',
    '1 PONT',
    '1 PAIRE DE TENONS FACE/BRANCHES',
    '1 PAIRE DE BRANCHES',
    '1 VERRE GAUCHE',
    '1 VERRE DROIT',
    '1 PAIRE DE VERRES',
    '1 MASQUE',
    'PLAQUETTES',
    'RX : MONTURE ET VERRES',
    'LOGO',
    'STRASS',
    'VIS DE BRANCHE/FACE',
    '1 MONTURE NUE',
    'CAVALIER',
  ],
  suppliers: ['LUXOTTICA', 'SAFILO', 'OPAL', 'MARCOLIN'],
  supplierAttachmentRules: [
    { supplier: 'CHARMANT', fullFramePhoto: true, defectivePartPhoto: true },
    { supplier: 'SAFILO', fullFramePhoto: true, defectivePartPhoto: true },
    { supplier: 'MARCOLIN', fullFramePhoto: true, defectivePartPhoto: true },
    { supplier: 'DE RIGO', fullFramePhoto: false, defectivePartPhoto: true },
  ],
  brandAttachmentRules: [
    { brand: 'OLIVER PEOPLES', fullFramePhoto: false, defectivePartPhoto: true },
    { brand: 'KENDALL & KYLIE', fullFramePhoto: true, defectivePartPhoto: true },
    { brand: 'SAFILO', fullFramePhoto: true, defectivePartPhoto: true },
  ],
  supplierBrandRules: [],
  brands: [
    'AD LIB',
    'ADIDAS',
    'ADIDAS JUNIOR',
    'AIRLIGHT',
    'ALEXANDER MAC QUEEN',
    'ALIBI',
    'ANA HICKMANN',
    'ANDY BROOK',
    'ARNETTE',
    'ART KENTIEL',
    'Attitude Santé',
    'AVENGERS',
    'AWEAR',
    'AZUR',
    'BAZZARO',
    'B JENIE HILLS',
    'B KOSBY SON',
    'BA&SH',
    'BALI EYEWEAR',
    'RAY BAN',
    'OAKLEY',
    'VOGUE',
    'CARRERA',
    'VERSACE',
    'DOLCE & GABANA',
    'GUESS',
  ],
  warranties: ['Garantie fournisseur', 'Garantie interne payante', 'Hors garantie', 'Commande client à facturer'],
  completeFrameAllowedSuppliers: ['LUXOTTICA', 'SAFILO'],
};

function normalizeSupplierAttachmentRules(rules = []) {
  return rules
    .map((rule) => ({
      supplier: String(rule?.supplier || '').trim().toUpperCase(),
      fullFramePhoto: Boolean(rule?.fullFramePhoto),
      defectivePartPhoto: Boolean(rule?.defectivePartPhoto),
      serialNumberRequired: Boolean(rule?.serialNumberRequired),
      mandatoryForm: Boolean(rule?.mandatoryForm),
      purchaseInvoice: Boolean(rule?.purchaseInvoice),
      defectVideoRequired: Boolean(rule?.defectVideoRequired),
    }))
    .filter((rule) => rule.supplier);
}

function normalizeBrandAttachmentRules(rules = []) {
  return rules
    .map((rule) => ({
      brand: String(rule?.brand || '').trim().toUpperCase(),
      fullFramePhoto: Boolean(rule?.fullFramePhoto),
      defectivePartPhoto: Boolean(rule?.defectivePartPhoto),
      serialNumberRequired: Boolean(rule?.serialNumberRequired),
      mandatoryForm: Boolean(rule?.mandatoryForm),
      purchaseInvoice: Boolean(rule?.purchaseInvoice),
      defectVideoRequired: Boolean(rule?.defectVideoRequired),
    }))
    .filter((rule) => rule.brand);
}

function normalizeSupplierBrandRules(rules = [], brands = []) {
  const brandByKey = new Map(uniqueList(brands).map((brand) => [String(brand).trim().toUpperCase(), brand]));
  return (Array.isArray(rules) ? rules : [])
    .map((rule) => ({
      supplier: String(rule?.supplier || '').trim().toUpperCase(),
      brands: uniqueList(rule?.brands || [])
        .map((brand) => brandByKey.get(String(brand).trim().toUpperCase()))
        .filter(Boolean),
    }))
    .filter((rule, index, source) =>
      rule.supplier &&
      rule.brands.length &&
      source.findIndex((candidate) => candidate.supplier === rule.supplier) === index
    );
}

function withSequentialOrder(list) {
  return list.map((item, index) => ({ ...item, order: index + 1 }));
}

function EditableList({ title, helperText, values, onChange, defaults = [], disabled = false, minRows = 3 }) {
  const updateValue = (index, value) => {
    onChange(values.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const removeValue = (index) => {
    if (!window.confirm(`Supprimer "${values[index] || 'cette ligne'}" de la liste ${title} ?`)) return;
    onChange(values.filter((_item, itemIndex) => itemIndex !== index));
  };

  const addValue = () => {
    onChange([...values, '']);
  };

  const resetValues = () => {
    onChange(defaults);
  };

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={resetValues} disabled={disabled || !defaults.length}>
            <RestartAltRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" onClick={addValue} disabled={disabled}>
            <AddRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack spacing={1} sx={{ minHeight: minRows * 42 }}>
        {values.map((value, index) => (
          <Stack key={`${title}-${index}`} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              value={value}
              onChange={(event) => updateValue(index, event.target.value)}
              disabled={disabled}
              fullWidth
            />
            <IconButton size="small" color="error" onClick={() => removeValue(index)} disabled={disabled}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {!values.length && (
          <Alert severity="warning" variant="outlined">
            Liste vide. Ajoutez au moins une valeur ou réinitialisez.
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}

function SupplierAutocompleteList({ title, helperText, values, options, onChange, defaults = [], disabled = false, minRows = 3 }) {
  const supplierOptions = uniqueList(options).sort((left, right) => left.localeCompare(right, 'fr'));
  const selectedKeys = new Set(values.map((value) => String(value || '').trim().toUpperCase()).filter(Boolean));

  const updateValue = (index, value) => {
    onChange(values.map((item, itemIndex) => (itemIndex === index ? value || '' : item)));
  };

  const removeValue = (index) => {
    if (!window.confirm(`Supprimer "${values[index] || 'cette ligne'}" de la liste ${title} ?`)) return;
    onChange(values.filter((_item, itemIndex) => itemIndex !== index));
  };

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{helperText}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => onChange(defaults)} disabled={disabled || !defaults.length}>
            <RestartAltRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" onClick={() => onChange([...values, ''])} disabled={disabled || values.some((value) => !value)}>
            <AddRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack spacing={1} sx={{ minHeight: minRows * 42 }}>
        {values.map((value, index) => (
          <Stack key={`${title}-${index}`} direction="row" spacing={1} alignItems="center">
            <Autocomplete
              size="small"
              options={supplierOptions.filter((option) => option === value || !selectedKeys.has(option.toUpperCase()))}
              value={value || null}
              onChange={(_event, nextValue) => updateValue(index, nextValue)}
              disabled={disabled}
              disableClearable={Boolean(value)}
              renderInput={(params) => <TextField {...params} label="Fournisseur existant" placeholder="Sélectionner un fournisseur" />}
              fullWidth
            />
            <IconButton size="small" color="error" onClick={() => removeValue(index)} disabled={disabled}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {!values.length && (
          <Alert severity="warning" variant="outlined">Aucun fournisseur autorisé. Utilisez + pour en sélectionner un.</Alert>
        )}
      </Stack>
    </Stack>
  );
}

function SupplierBrandTransfer({ suppliers, brands, rules, onChange, disabled = false }) {
  const [supplier, setSupplier] = useState('');
  const [availableSelection, setAvailableSelection] = useState([]);
  const [assignedSelection, setAssignedSelection] = useState([]);
  const [availableSearch, setAvailableSearch] = useState('');
  const normalizedSupplier = String(supplier || '').trim().toUpperCase();
  const assignedBrands = normalizedSupplier
    ? (rules.find((rule) => rule.supplier === normalizedSupplier)?.brands || [])
    : [];
  const assignedKeys = new Set(assignedBrands.map((brand) => String(brand).toUpperCase()));
  const availableBrands = uniqueList(brands).filter((brand) => !assignedKeys.has(String(brand).toUpperCase()));
  const filteredAvailableBrands = availableBrands.filter((brand) =>
    brand.toLocaleLowerCase('fr').includes(availableSearch.trim().toLocaleLowerCase('fr'))
  );

  const selectSupplier = (_event, value) => {
    setSupplier(value || '');
    setAvailableSelection([]);
    setAssignedSelection([]);
    setAvailableSearch('');
  };

  const toggle = (value, setter) => {
    setter((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  const updateAssignedBrands = (nextBrands) => {
    const nextRules = rules.filter((rule) => rule.supplier !== normalizedSupplier);
    if (nextBrands.length) {
      nextRules.push({ supplier: normalizedSupplier, brands: nextBrands });
    }
    onChange(normalizeSupplierBrandRules(nextRules, brands));
  };

  const assignSelected = () => {
    updateAssignedBrands(uniqueList([...assignedBrands, ...availableSelection]));
    setAvailableSelection([]);
  };

  const removeSelected = () => {
    const selectedKeys = new Set(assignedSelection.map((brand) => String(brand).toUpperCase()));
    updateAssignedBrands(assignedBrands.filter((brand) => !selectedKeys.has(String(brand).toUpperCase())));
    setAssignedSelection([]);
  };

  const renderList = (values, selected, setSelected, emptyLabel) => (
    <List dense disablePadding sx={{ height: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      {values.map((brand) => (
        <ListItem key={brand} disablePadding>
          <ListItemButton dense disabled={disabled || !normalizedSupplier} onClick={() => toggle(brand, setSelected)}>
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Checkbox edge="start" size="small" checked={selected.includes(brand)} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <ListItemText primary={brand} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItemButton>
        </ListItem>
      ))}
      {!values.length && (
        <ListItem>
          <ListItemText primary={emptyLabel} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
        </ListItem>
      )}
    </List>
  );

  return (
    <Stack spacing={1.25}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Marques par fournisseur</Typography>
        <Typography variant="caption" color="text.secondary">
          Une marque associée sera proposée uniquement pour ce fournisseur. Sans association, toutes les marques restent disponibles.
        </Typography>
      </Box>
      <Autocomplete
        size="small"
        options={uniqueList(suppliers).sort((left, right) => left.localeCompare(right, 'fr'))}
        value={supplier}
        onChange={selectSupplier}
        disabled={disabled}
        sx={{ maxWidth: 420 }}
        renderInput={(params) => <TextField {...params} label="Rechercher un fournisseur" placeholder="Nom du fournisseur" />}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 56px minmax(0, 1fr)' }, gap: 1, alignItems: 'center' }}>
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800 }}>Marques disponibles</Typography>
            <TextField
              size="small"
              value={availableSearch}
              onChange={(event) => setAvailableSearch(event.target.value)}
              placeholder="Rechercher"
              disabled={disabled || !normalizedSupplier}
              inputProps={{ 'aria-label': 'Rechercher une marque disponible' }}
              sx={{ width: 190 }}
            />
          </Stack>
          {renderList(filteredAvailableBrands, availableSelection, setAvailableSelection, availableSearch ? 'Aucune marque trouvée.' : 'Aucune marque disponible.')}
        </Box>
        <Stack spacing={1} alignItems="center" justifyContent="center">
          <IconButton color="primary" onClick={assignSelected} disabled={disabled || !normalizedSupplier || !availableSelection.length} aria-label="Associer les marques sélectionnées">
            <ArrowForwardRoundedIcon />
          </IconButton>
          <IconButton color="primary" onClick={removeSelected} disabled={disabled || !normalizedSupplier || !assignedSelection.length} aria-label="Retirer les marques sélectionnées">
            <ArrowBackRoundedIcon />
          </IconButton>
        </Stack>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>Marques associées</Typography>
          {renderList(assignedBrands, assignedSelection, setAssignedSelection, 'Aucune association : toutes les marques sont autorisées.')}
        </Box>
      </Box>
    </Stack>
  );
}

export default function SettingsPage() {
  const menuPage = useMenuPage('/settings');
  const [lowStockThreshold, setLowStockThreshold] = useState(2);
  const [stockReplenishmentLookbackDays, setStockReplenishmentLookbackDays] = useState(90);
  const [workshopBlockedDays, setWorkshopBlockedDays] = useState(3);
  const [workshopReceptionAlertDays, setWorkshopReceptionAlertDays] = useState(4);
  const [logisticsBlockedDays, setLogisticsBlockedDays] = useState(3);
  const [mountingTypes, setMountingTypes] = useState(defaultMountingTypes);
  const [nonConformityScopes, setNonConformityScopes] = useState(defaultNonConformityScopes);
  const [nonConformityReasons, setNonConformityReasons] = useState(defaultNonConformityReasons);
  const [nonConformityNotificationEmail, setNonConformityNotificationEmail] = useState('');
  const [nonConformityNotificationEmails, setNonConformityNotificationEmails] = useState('');
  const [traceabilityNotificationEmails, setTraceabilityNotificationEmails] = useState('');
  const [smtpSettings, setSmtpSettings] = useState(defaultSmtpSettings);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestSending, setSmtpTestSending] = useState(false);
  const [smtpTestMessage, setSmtpTestMessage] = useState('');
  const [smtpTestError, setSmtpTestError] = useState('');
  const [storeTypes, setStoreTypes] = useState(['OPTIQUE DE BOURBON', 'ALAIN AFFLELOU']);
  const [savDefects, setSavDefects] = useState(defaultSavSettings.defects);
  const [savReplacementParts, setSavReplacementParts] = useState(defaultSavSettings.replacementParts);
  const [savSuppliers, setSavSuppliers] = useState(defaultSavSettings.suppliers);
  const [supplierAttachmentRules, setSupplierAttachmentRules] = useState(defaultSavSettings.supplierAttachmentRules);
  const [brandAttachmentRules, setBrandAttachmentRules] = useState(defaultSavSettings.brandAttachmentRules);
  const [supplierBrandRules, setSupplierBrandRules] = useState(defaultSavSettings.supplierBrandRules);
  const [savBrands, setSavBrands] = useState(defaultSavSettings.brands);
  const [savWarranties, setSavWarranties] = useState(defaultSavSettings.warranties);
  const [completeFrameAllowedSuppliers, setCompleteFrameAllowedSuppliers] = useState(defaultSavSettings.completeFrameAllowedSuppliers);
  const [menuConfig, setMenuConfig] = useState(() => normalizeMenuConfig(DEFAULT_MENU_CONFIG));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [stockPayload, sitesPayload, savPayload, menuPayload, smtpPayload, suppliersPayload] = await Promise.all([
        requestJson('/settings/STOCK'),
        requestJson('/settings/SITES'),
        requestJson('/settings/SAV'),
        requestJson('/settings/MENU'),
        requestJson('/settings/SMTP'),
        requestJson('/suppliers'),
      ]);
      setLowStockThreshold(Number(stockPayload.value?.lowStockThreshold ?? 2));
      setStockReplenishmentLookbackDays(Number(stockPayload.value?.stockReplenishmentLookbackDays ?? 90));
      setWorkshopBlockedDays(Number(stockPayload.value?.workshopBlockedDays ?? 3));
      setWorkshopReceptionAlertDays(Number(stockPayload.value?.workshopReceptionAlertDays ?? 4));
      setLogisticsBlockedDays(Number(stockPayload.value?.logisticsBlockedDays ?? 3));
      setNonConformityNotificationEmail(stockPayload.value?.nonConformityNotificationEmail || '');
      setNonConformityNotificationEmails(
        Array.isArray(stockPayload.value?.nonConformityNotificationEmails)
          ? stockPayload.value.nonConformityNotificationEmails.join('\n')
          : (stockPayload.value?.nonConformityNotificationEmail || '')
      );
      setTraceabilityNotificationEmails(
        Array.isArray(stockPayload.value?.traceabilityNotificationEmails)
          ? stockPayload.value.traceabilityNotificationEmails.join('\n')
          : (stockPayload.value?.traceabilityNotificationEmail || '')
      );
      setMountingTypes(uniqueList(stockPayload.value?.mountingTypes || defaultMountingTypes));
      const loadedScopes = normalizeNonConformityScopes(stockPayload.value?.nonConformityScopes || defaultNonConformityScopes);
      setNonConformityScopes(loadedScopes);
      setNonConformityReasons(
        normalizeNonConformityReasons(stockPayload.value?.nonConformityReasons || defaultNonConformityReasons, loadedScopes)
      );
      setSmtpSettings({
        ...defaultSmtpSettings,
        ...(smtpPayload.value || {}),
        password: '',
        profiles: {
          ...defaultSmtpSettings.profiles,
          ...(smtpPayload.value?.profiles || {}),
        },
      });
      setStoreTypes(uniqueList(sitesPayload.value?.storeTypes || ['OPTIQUE DE BOURBON', 'ALAIN AFFLELOU']));
      setSavDefects(uniqueList(savPayload.value?.defects || defaultSavSettings.defects));
      setSavReplacementParts(uniqueList(savPayload.value?.replacementParts || defaultSavSettings.replacementParts));
      const loadedSuppliers = uniqueList(getCollection(suppliersPayload).filter((supplier) => supplier.active !== false).map((supplier) => supplier.name));
      setSavSuppliers(loadedSuppliers.length ? loadedSuppliers : uniqueList(savPayload.value?.suppliers || defaultSavSettings.suppliers));
      setSupplierAttachmentRules(
        normalizeSupplierAttachmentRules(savPayload.value?.supplierAttachmentRules || defaultSavSettings.supplierAttachmentRules)
      );
      setBrandAttachmentRules(
        normalizeBrandAttachmentRules(savPayload.value?.brandAttachmentRules || defaultSavSettings.brandAttachmentRules)
      );
      setSavBrands(uniqueList(savPayload.value?.brands || defaultSavSettings.brands));
      setSupplierBrandRules(
        normalizeSupplierBrandRules(savPayload.value?.supplierBrandRules || defaultSavSettings.supplierBrandRules, savPayload.value?.brands || defaultSavSettings.brands)
      );
      setSavWarranties(uniqueList(savPayload.value?.warranties || defaultSavSettings.warranties));
      setCompleteFrameAllowedSuppliers(uniqueList(savPayload.value?.completeFrameAllowedSuppliers || defaultSavSettings.completeFrameAllowedSuppliers));
      setMenuConfig(normalizeMenuConfig(menuPayload.value));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSection = async (section) => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      if (section === 'stock') {
        await requestJson('/settings/STOCK', {
          method: 'PUT',
          body: JSON.stringify({
            label: 'Paramètres stock',
            value: {
              lowStockThreshold: Number(lowStockThreshold || 0),
              stockReplenishmentLookbackDays: Number(stockReplenishmentLookbackDays || 90),
              workshopBlockedDays: Number(workshopBlockedDays || 0),
              workshopReceptionAlertDays: Number(workshopReceptionAlertDays || 4),
              logisticsBlockedDays: Number(logisticsBlockedDays || 0),
              nonConformityNotificationEmail: nonConformityNotificationEmail.trim(),
              nonConformityNotificationEmails: [...new Set(nonConformityNotificationEmails.split(/[\n,;]+/).map((email) => email.trim().toLowerCase()).filter(Boolean))],
              traceabilityNotificationEmail: traceabilityNotificationEmails.split(/[\n,;]+/).map((email) => email.trim().toLowerCase()).filter(Boolean)[0] || '',
              traceabilityNotificationEmails: [...new Set(traceabilityNotificationEmails.split(/[\n,;]+/).map((email) => email.trim().toLowerCase()).filter(Boolean))],
              mountingTypes: uniqueList(mountingTypes),
              nonConformityScopes: normalizeNonConformityScopes(nonConformityScopes),
              nonConformityReasons: normalizeNonConformityReasons(nonConformityReasons, normalizeNonConformityScopes(nonConformityScopes)),
            },
          }),
        });
        setMountingTypes(uniqueList(mountingTypes));
        const normalizedScopes = normalizeNonConformityScopes(nonConformityScopes);
        setNonConformityScopes(normalizedScopes);
        setNonConformityReasons(normalizeNonConformityReasons(nonConformityReasons, normalizedScopes));
      }

      if (section === 'smtp') {
        const smtpPayload = await requestJson('/settings/SMTP', {
          method: 'PUT',
          body: JSON.stringify({
            label: 'Paramètres SMTP',
            value: {
              profiles: {
                global: {
                  enabled: Boolean(smtpSettings.enabled),
                  host: smtpSettings.host,
                  port: Number(smtpSettings.port || 587),
                  secure: Boolean(smtpSettings.secure),
                  user: smtpSettings.user,
                  password: smtpSettings.password,
                  fromName: smtpSettings.fromName,
                  fromEmail: smtpSettings.fromEmail,
                  replyTo: smtpSettings.replyTo,
                },
                logistics: {
                  ...(smtpSettings.profiles?.logistics || {}),
                  inheritGlobal: true,
                },
                workshop: {
                  ...(smtpSettings.profiles?.workshop || {}),
                  inheritGlobal: true,
                },
              },
            },
          }),
        });
        setSmtpSettings({
          ...defaultSmtpSettings,
          ...(smtpPayload.value || {}),
          password: '',
          profiles: {
            ...defaultSmtpSettings.profiles,
            ...(smtpPayload.value?.profiles || {}),
          },
        });
      }

      if (section === 'sites') {
        await requestJson('/settings/SITES', {
          method: 'PUT',
          body: JSON.stringify({
            label: 'Paramètres sites',
            value: {
              storeTypes: uniqueList(storeTypes),
            },
          }),
        });
        setStoreTypes(uniqueList(storeTypes));
      }

      if (section === 'sav') {
        await requestJson('/settings/SAV', {
          method: 'PUT',
          body: JSON.stringify({
            label: 'Paramètres Logistique',
            value: {
              defects: uniqueList(savDefects),
              replacementParts: uniqueList(savReplacementParts),
              brands: uniqueList(savBrands),
              warranties: uniqueList(savWarranties),
              completeFrameAllowedSuppliers: uniqueList(completeFrameAllowedSuppliers),
              supplierAttachmentRules: normalizeSupplierAttachmentRules(
                uniqueList(savSuppliers).map((supplier) => {
                  const rule = supplierAttachmentRules.find((item) => item.supplier === supplier.toUpperCase()) || {};
                  return {
                    supplier,
                    fullFramePhoto: Boolean(rule.fullFramePhoto),
                    defectivePartPhoto: Boolean(rule.defectivePartPhoto),
                    serialNumberRequired: Boolean(rule.serialNumberRequired),
                    mandatoryForm: Boolean(rule.mandatoryForm),
                    purchaseInvoice: Boolean(rule.purchaseInvoice),
                    defectVideoRequired: Boolean(rule.defectVideoRequired),
                  };
                })
              ).filter((rule) => rule.fullFramePhoto || rule.defectivePartPhoto || rule.serialNumberRequired || rule.mandatoryForm || rule.purchaseInvoice || rule.defectVideoRequired),
              brandAttachmentRules: normalizeBrandAttachmentRules(
                uniqueList(savBrands).map((brand) => {
                  const rule = brandAttachmentRules.find((item) => item.brand === brand.toUpperCase()) || {};
                  return {
                    brand,
                    fullFramePhoto: Boolean(rule.fullFramePhoto),
                    defectivePartPhoto: Boolean(rule.defectivePartPhoto),
                    serialNumberRequired: Boolean(rule.serialNumberRequired),
                    mandatoryForm: Boolean(rule.mandatoryForm),
                    purchaseInvoice: Boolean(rule.purchaseInvoice),
                    defectVideoRequired: Boolean(rule.defectVideoRequired),
                  };
                })
              ).filter((rule) => rule.fullFramePhoto || rule.defectivePartPhoto || rule.serialNumberRequired || rule.mandatoryForm || rule.purchaseInvoice || rule.defectVideoRequired),
              supplierBrandRules: normalizeSupplierBrandRules(supplierBrandRules, savBrands),
            },
          }),
        });
        setSavDefects(uniqueList(savDefects));
        setSavReplacementParts(uniqueList(savReplacementParts));
        setSavBrands(uniqueList(savBrands));
        setSavWarranties(uniqueList(savWarranties));
        setCompleteFrameAllowedSuppliers(uniqueList(completeFrameAllowedSuppliers));
        setSupplierBrandRules(normalizeSupplierBrandRules(supplierBrandRules, savBrands));
        setSupplierAttachmentRules(
          normalizeSupplierAttachmentRules(
            uniqueList(savSuppliers).map((supplier) => {
              const rule = supplierAttachmentRules.find((item) => item.supplier === supplier.toUpperCase()) || {};
              return {
                supplier,
                fullFramePhoto: Boolean(rule.fullFramePhoto),
                defectivePartPhoto: Boolean(rule.defectivePartPhoto),
                serialNumberRequired: Boolean(rule.serialNumberRequired),
                mandatoryForm: Boolean(rule.mandatoryForm),
                purchaseInvoice: Boolean(rule.purchaseInvoice),
                defectVideoRequired: Boolean(rule.defectVideoRequired),
              };
            })
          ).filter((rule) => rule.fullFramePhoto || rule.defectivePartPhoto || rule.serialNumberRequired || rule.mandatoryForm || rule.purchaseInvoice || rule.defectVideoRequired)
        );
        setBrandAttachmentRules(
          normalizeBrandAttachmentRules(
            uniqueList(savBrands).map((brand) => {
              const rule = brandAttachmentRules.find((item) => item.brand === brand.toUpperCase()) || {};
              return {
                brand,
                fullFramePhoto: Boolean(rule.fullFramePhoto),
                defectivePartPhoto: Boolean(rule.defectivePartPhoto),
                serialNumberRequired: Boolean(rule.serialNumberRequired),
                mandatoryForm: Boolean(rule.mandatoryForm),
                purchaseInvoice: Boolean(rule.purchaseInvoice),
                defectVideoRequired: Boolean(rule.defectVideoRequired),
              };
            })
          ).filter((rule) => rule.fullFramePhoto || rule.defectivePartPhoto || rule.serialNumberRequired || rule.mandatoryForm || rule.purchaseInvoice || rule.defectVideoRequired)
        );
      }

      if (section === 'menu') {
        await requestJson('/settings/MENU', {
          method: 'PUT',
          body: JSON.stringify({
            label: 'Paramètres menu',
            value: menuConfig,
          }),
        });
        window.dispatchEvent(new Event('opticdev:menu-config-updated'));
      }

      setMessage('Paramètres enregistrés.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendSmtpTestEmail = async () => {
    try {
      setSmtpTestSending(true);
      setSmtpTestMessage('');
      setSmtpTestError('');
      const payload = await requestJson('/settings/SMTP/test-email', {
        method: 'POST',
        body: JSON.stringify({ to: smtpTestEmail }),
      });
      setSmtpTestMessage(payload.message || 'Email de test envoyé.');
    } catch (err) {
      setSmtpTestError(err.message);
    } finally {
      setSmtpTestSending(false);
    }
  };


  const updateSmtpSetting = (field, value) => {
    setSmtpSettings((current) => ({ ...current, [field]: value }));
  };

  const updateSmtpProfileSetting = (profile, field, value) => {
    setSmtpSettings((current) => ({
      ...current,
      profiles: {
        ...current.profiles,
        [profile]: {
          ...(current.profiles?.[profile] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateNonConformityScope = (scopeValue, label) => {
    setNonConformityScopes((current) =>
      current.map((scope) => (scope.value === scopeValue ? { ...scope, label } : scope))
    );
  };

  const addNonConformityScope = () => {
    const value = `zone_${Date.now()}`;
    setNonConformityScopes((current) => [...current, { value, label: 'Nouvelle zone' }]);
  };

  const removeNonConformityScope = (scopeValue) => {
    setNonConformityScopes((current) => current.filter((scope) => scope.value !== scopeValue));
    setNonConformityReasons((current) =>
      current
        .map((reason) => ({ ...reason, scopes: reason.scopes.filter((scope) => scope !== scopeValue) }))
        .filter((reason) => reason.scopes.length)
    );
  };

  const updateNonConformityReason = (reasonValue, label) => {
    setNonConformityReasons((current) =>
      current.map((reason) => (reason.value === reasonValue ? { ...reason, label } : reason))
    );
  };

  const toggleNonConformityReasonScope = (reasonValue, scopeValue, checked) => {
    setNonConformityReasons((current) =>
      current.map((reason) => {
        if (reason.value !== reasonValue) return reason;
        const scopes = checked
          ? [...new Set([...reason.scopes, scopeValue])]
          : reason.scopes.filter((scope) => scope !== scopeValue);
        return { ...reason, scopes };
      })
    );
  };

  const addNonConformityReason = () => {
    const value = `motif_${Date.now()}`;
    setNonConformityReasons((current) => [
      ...current,
      { value, label: 'Nouveau motif', scopes: nonConformityScopes.slice(0, 1).map((scope) => scope.value) },
    ]);
  };

  const removeNonConformityReason = (reasonValue) => {
    setNonConformityReasons((current) => current.filter((reason) => reason.value !== reasonValue));
  };

  const handleGroupChange = (groupId, field, value) => {
    setMenuConfig((current) => ({
      ...current,
      groups: current.groups.map((group) =>
        group.id === groupId
          ? { ...group, [field]: field === 'order' ? Number(value || 0) : value }
          : group
      ),
    }));
  };

  const handleItemChange = (itemId, field, value) => {
    setMenuConfig((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === itemId
          ? { ...item, [field]: field === 'order' ? Number(value || 0) : value }
          : item
      ),
    }));
  };

  const addMenuGroup = () => {
    const nextIndex = menuConfig.groups.length + 1;
    setMenuConfig((current) => ({
      ...current,
      groups: [
        ...current.groups,
        {
          id: `section-${Date.now()}`,
          label: `Section ${nextIndex}`,
          icon: 'folder',
          order: nextIndex,
        },
      ],
    }));
  };

  const moveGroup = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;

    setMenuConfig((current) => {
      const groups = [...current.groups].sort((a, b) => a.order - b.order);
      const sourceIndex = groups.findIndex((group) => group.id === sourceId);
      const targetIndex = groups.findIndex((group) => group.id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return current;

      const [sourceGroup] = groups.splice(sourceIndex, 1);
      groups.splice(targetIndex, 0, sourceGroup);
      return { ...current, groups: withSequentialOrder(groups) };
    });
  };

  const moveItem = (sourceId, targetId, targetGroupId = '') => {
    if (!sourceId) return;
    if (sourceId === targetId) return;

    setMenuConfig((current) => {
      const sourceItem = current.items.find((item) => item.id === sourceId);
      if (!sourceItem) return current;

      const nextGroupId = targetGroupId || current.items.find((item) => item.id === targetId)?.groupId || sourceItem.groupId;
      const otherItems = current.items.filter((item) => item.id !== sourceId);
      const groupedItems = otherItems
        .filter((item) => item.groupId === nextGroupId)
        .sort((a, b) => a.order - b.order);
      const targetIndex = targetId
        ? groupedItems.findIndex((item) => item.id === targetId)
        : groupedItems.length;

      groupedItems.splice(targetIndex === -1 ? groupedItems.length : targetIndex, 0, {
        ...sourceItem,
        groupId: nextGroupId,
      });

      const reorderedGroupItems = withSequentialOrder(groupedItems);
      const untouchedItems = otherItems.filter((item) => item.groupId !== nextGroupId);

      return {
        ...current,
        items: [...untouchedItems, ...reorderedGroupItems].sort((a, b) => {
          if (a.groupId === b.groupId) return a.order - b.order;
          return a.groupId.localeCompare(b.groupId);
        }),
      };
    });
  };

  const handleDragStart = (type, id) => (event) => {
    setDragging({ type, id });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${type}:${id}`);
  };

  const handleDragEnd = () => {
    setDragging(null);
  };

  const getSupplierAttachmentRule = (supplier) =>
    supplierAttachmentRules.find((rule) => rule.supplier === String(supplier || '').trim().toUpperCase()) || {
      supplier: String(supplier || '').trim().toUpperCase(),
      fullFramePhoto: false,
      defectivePartPhoto: false,
      serialNumberRequired: false,
      mandatoryForm: false,
      purchaseInvoice: false,
      defectVideoRequired: false,
    };

  const updateSupplierAttachmentRule = (supplier, field, checked) => {
    const normalizedSupplier = String(supplier || '').trim().toUpperCase();
    setSupplierAttachmentRules((current) => {
      const existing = current.find((rule) => rule.supplier === normalizedSupplier) || {
        supplier: normalizedSupplier,
        fullFramePhoto: false,
        defectivePartPhoto: false,
        serialNumberRequired: false,
        mandatoryForm: false,
        purchaseInvoice: false,
        defectVideoRequired: false,
      };
      const nextRule = { ...existing, supplier: normalizedSupplier, [field]: checked };
      const withoutCurrent = current.filter((rule) => rule.supplier !== normalizedSupplier);
      if (!nextRule.fullFramePhoto && !nextRule.defectivePartPhoto && !nextRule.serialNumberRequired && !nextRule.mandatoryForm && !nextRule.purchaseInvoice && !nextRule.defectVideoRequired) return withoutCurrent;
      return [...withoutCurrent, nextRule].sort((a, b) => a.supplier.localeCompare(b.supplier));
    });
  };

  const getBrandAttachmentRule = (brand) =>
    brandAttachmentRules.find((rule) => rule.brand === String(brand || '').trim().toUpperCase()) || {
      brand: String(brand || '').trim().toUpperCase(),
      fullFramePhoto: false,
      defectivePartPhoto: false,
      serialNumberRequired: false,
      mandatoryForm: false,
      purchaseInvoice: false,
      defectVideoRequired: false,
    };

  const updateBrandAttachmentRule = (brand, field, checked) => {
    const normalizedBrand = String(brand || '').trim().toUpperCase();
    setBrandAttachmentRules((current) => {
      const existing = current.find((rule) => rule.brand === normalizedBrand) || {
        brand: normalizedBrand,
        fullFramePhoto: false,
        defectivePartPhoto: false,
        serialNumberRequired: false,
        mandatoryForm: false,
        purchaseInvoice: false,
        defectVideoRequired: false,
      };
      const nextRule = { ...existing, brand: normalizedBrand, [field]: checked };
      const withoutCurrent = current.filter((rule) => rule.brand !== normalizedBrand);
      if (!nextRule.fullFramePhoto && !nextRule.defectivePartPhoto && !nextRule.serialNumberRequired && !nextRule.mandatoryForm && !nextRule.purchaseInvoice && !nextRule.defectVideoRequired) return withoutCurrent;
      return [...withoutCurrent, nextRule].sort((a, b) => a.brand.localeCompare(b.brand));
    });
  };

  const displayedGroups = [...menuConfig.groups].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  const groupOrderMap = new Map(displayedGroups.map((group, index) => [group.id, index]));
  const displayedItems = [...menuConfig.items].sort((a, b) => {
    const groupCompare = (groupOrderMap.get(a.groupId) ?? 999) - (groupOrderMap.get(b.groupId) ?? 999);
    if (groupCompare) return groupCompare;
    return a.order - b.order || a.label.localeCompare(b.label);
  });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 950 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Box>
        <Stack spacing={3} sx={{ maxWidth: 1040 }}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsRoundedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Stock
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Définit à partir de quelle quantité un verre passe en alerte stock bas.
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Seuil stock bas"
                    type="number"
                    value={lowStockThreshold}
                    onChange={(event) => setLowStockThreshold(Number(event.target.value))}
                    inputProps={{ min: 0, step: 1 }}
                    disabled={loading}
                    helperText={`Warning quand disponible ≤ ${lowStockThreshold || 0}.`}
                    fullWidth
                  />
                  <TextField
                    label="Période sorties réassort"
                    type="number"
                    value={stockReplenishmentLookbackDays}
                    onChange={(event) => setStockReplenishmentLookbackDays(Number(event.target.value))}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                    helperText="Nombre de jours de sorties utilisées pour proposer la quantité à commander."
                    fullWidth
                  />
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => {
                      setLowStockThreshold(2);
                      setStockReplenishmentLookbackDays(90);
                    }}
                    disabled={saving || loading}
                  >
                    Réinitialiser Stock
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveSection('stock')}
                    disabled={saving || loading}
                    sx={{ fontWeight: 900 }}
                  >
                    Enregistrer Stock
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsRoundedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Suivi atelier
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Paramètres des workflows atelier, types de montage et non-conformités NCA / NCM.
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Alerte suivi atelier bloqué"
                    type="number"
                    value={workshopBlockedDays}
                    onChange={(event) => setWorkshopBlockedDays(Number(event.target.value))}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                    helperText="Nombre de jours sans changement d'étape."
                    fullWidth
                  />
                  <TextField
                    label="Alerte non reçu atelier"
                    type="number"
                    value={workshopReceptionAlertDays}
                    onChange={(event) => setWorkshopReceptionAlertDays(Number(event.target.value))}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                    helperText="Jours après l’envoi magasin sans réception atelier."
                    fullWidth
                  />
                  <TextField
                    label="Alerte Logistique bloquée"
                    type="number"
                    value={logisticsBlockedDays}
                    onChange={(event) => setLogisticsBlockedDays(Number(event.target.value))}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                    helperText="Nombre de jours sans changement de statut."
                    fullWidth
                  />
                </Box>

                <TextField
                  label="Destinataires email NCA / NCM"
                  value={nonConformityNotificationEmails}
                  onChange={(event) => setNonConformityNotificationEmails(event.target.value)}
                  disabled={loading}
                  multiline
                  minRows={2}
                  helperText="Une adresse par ligne (ou séparées par virgule/point-virgule). Le magasin reste ajouté automatiquement pour les NCA."
                  fullWidth
                />

                <TextField
                  label="Destinataires email Traçabilité"
                  value={traceabilityNotificationEmails}
                  onChange={(event) => setTraceabilityNotificationEmails(event.target.value)}
                  disabled={loading}
                  multiline
                  minRows={2}
                  helperText="Adresses destinataires lors d’une déclaration de traçabilité : une par ligne ou séparées par virgule."
                  fullWidth
                />

                <EditableList
                  title="Types de montage"
                  helperText="Liste utilisée dans le suivi atelier lors de l'envoi magasin."
                  values={mountingTypes}
                  onChange={setMountingTypes}
                  defaults={defaultMountingTypes}
                  disabled={loading}
                  minRows={6}
                />

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Non-conformités NCA / NCM
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Le motif est choisi en premier. Les zones proposées dépendent des zones cochées pour ce motif.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' }, gap: 2 }}>
                    <Stack spacing={1.2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Zones concernées
                        </Typography>
                        <Button size="small" startIcon={<AddRoundedIcon />} onClick={addNonConformityScope} disabled={loading}>
                          Ajouter
                        </Button>
                      </Stack>
                      {nonConformityScopes.map((scope) => (
                        <Stack key={scope.value} direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            value={scope.label}
                            onChange={(event) => updateNonConformityScope(scope.value, event.target.value)}
                            disabled={loading}
                            fullWidth
                          />
                          <IconButton size="small" color="error" onClick={() => removeNonConformityScope(scope.value)} disabled={loading || nonConformityScopes.length <= 1}>
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>

                    <Stack spacing={1.2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          Motifs et zones autorisées
                        </Typography>
                        <Button size="small" startIcon={<AddRoundedIcon />} onClick={addNonConformityReason} disabled={loading || !nonConformityScopes.length}>
                          Ajouter
                        </Button>
                      </Stack>
                      {nonConformityReasons.map((reason) => (
                        <Box key={reason.value} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                size="small"
                                label="Motif"
                                value={reason.label}
                                onChange={(event) => updateNonConformityReason(reason.value, event.target.value)}
                                disabled={loading}
                                fullWidth
                              />
                              <IconButton size="small" color="error" onClick={() => removeNonConformityReason(reason.value)} disabled={loading || nonConformityReasons.length <= 1}>
                                <DeleteRoundedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                              {nonConformityScopes.map((scope) => (
                                <FormControlLabel
                                  key={scope.value}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={reason.scopes.includes(scope.value)}
                                      onChange={(event) => toggleNonConformityReasonScope(reason.value, scope.value, event.target.checked)}
                                      disabled={loading}
                                    />
                                  }
                                  label={scope.label}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => {
                      setWorkshopBlockedDays(3);
                      setLogisticsBlockedDays(3);
                      setMountingTypes(defaultMountingTypes);
                      setNonConformityScopes(defaultNonConformityScopes);
                      setNonConformityReasons(defaultNonConformityReasons);
                    }}
                    disabled={saving || loading}
                  >
                    Réinitialiser Suivi atelier
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveSection('stock')}
                    disabled={saving || loading}
                    sx={{ fontWeight: 900 }}
                  >
                    Enregistrer Suivi atelier
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>


          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsRoundedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      SMTP
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configuration utilisée pour les futurs mails automatiques de réapprovisionnement fournisseur.
                    </Typography>
                  </Box>
                </Stack>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(smtpSettings.enabled)}
                      onChange={(event) => updateSmtpSetting('enabled', event.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Activer l'envoi automatique de mails"
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 120px 180px' }, gap: 1.5 }}>
                  <TextField
                    label="Serveur SMTP"
                    value={smtpSettings.host}
                    onChange={(event) => updateSmtpSetting('host', event.target.value)}
                    disabled={loading}
                    placeholder="smtp.exemple.fr"
                    fullWidth
                  />
                  <TextField
                    label="Port"
                    type="number"
                    value={smtpSettings.port}
                    onChange={(event) => updateSmtpSetting('port', Number(event.target.value))}
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(smtpSettings.secure)}
                        onChange={(event) => updateSmtpSetting('secure', event.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="SSL / TLS"
                    sx={{ alignSelf: 'center' }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Utilisateur SMTP"
                    value={smtpSettings.user}
                    onChange={(event) => updateSmtpSetting('user', event.target.value)}
                    disabled={loading}
                    helperText="Facultatif : laissez vide pour un relais SMTP sans authentification, par exemple smtp-relay.gmail.com autorisé par IP."
                    autoComplete="off"
                    fullWidth
                  />
                  <TextField
                    label="Mot de passe SMTP"
                    type="password"
                    value={smtpSettings.password}
                    onChange={(event) => updateSmtpSetting('password', event.target.value)}
                    disabled={loading}
                    helperText={smtpSettings.hasPassword ? 'Mot de passe déjà défini. Laissez vide pour le conserver.' : 'Renseignez le mot de passe SMTP si nécessaire.'}
                    autoComplete="new-password"
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Nom expéditeur"
                    value={smtpSettings.fromName}
                    onChange={(event) => updateSmtpSetting('fromName', event.target.value)}
                    disabled={loading}
                    fullWidth
                  />
                  <TextField
                    label="Email expéditeur"
                    type="email"
                    value={smtpSettings.fromEmail}
                    onChange={(event) => updateSmtpSetting('fromEmail', event.target.value)}
                    disabled={loading}
                    placeholder="no-reply@domaine.fr"
                    fullWidth
                  />
                  <TextField
                    label="Reply-To"
                    type="email"
                    value={smtpSettings.replyTo}
                    onChange={(event) => updateSmtpSetting('replyTo', event.target.value)}
                    disabled={loading}
                    placeholder="optionnel"
                    fullWidth
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
                    Expéditeurs par module
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ces adresses utilisent la connexion SMTP ci-dessus. Elles permettent d'identifier clairement les mails Logistique et Suivi atelier.
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Email expéditeur Logistique"
                    type="email"
                    value={smtpSettings.profiles?.logistics?.fromEmail || ''}
                    onChange={(event) => updateSmtpProfileSetting('logistics', 'fromEmail', event.target.value)}
                    disabled={loading}
                    placeholder={smtpSettings.fromEmail || 'logistique@domaine.fr'}
                    helperText="Utilisé pour les demandes SAV et les commandes fournisseurs Logistique."
                    fullWidth
                  />
                  <TextField
                    label="Email expéditeur Suivi atelier"
                    type="email"
                    value={smtpSettings.profiles?.workshop?.fromEmail || ''}
                    onChange={(event) => updateSmtpProfileSetting('workshop', 'fromEmail', event.target.value)}
                    disabled={loading}
                    placeholder={smtpSettings.fromEmail || 'atelier@domaine.fr'}
                    helperText="Utilisé pour les réassorts de verres et les notifications atelier."
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Formule de politesse Logistique"
                    value={smtpSettings.profiles?.logistics?.signature || ''}
                    onChange={(event) => updateSmtpProfileSetting('logistics', 'signature', event.target.value)}
                    disabled={loading}
                    placeholder="Bien cordialement,"
                    helperText="Ajoutée avant le bloc identité et coordonnées."
                    multiline
                    minRows={4}
                    fullWidth
                  />
                  <TextField
                    label="Formule de politesse Suivi atelier"
                    value={smtpSettings.profiles?.workshop?.signature || ''}
                    onChange={(event) => updateSmtpProfileSetting('workshop', 'signature', event.target.value)}
                    disabled={loading}
                    placeholder="Bien cordialement,"
                    helperText="Ajoutée avant le bloc identité et coordonnées."
                    multiline
                    minRows={4}
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  {[
                    { key: 'logistics', title: 'Bloc signature Logistique' },
                    { key: 'workshop', title: 'Bloc signature Suivi atelier' },
                  ].map(({ key, title }) => {
                    const profile = smtpSettings.profiles?.[key] || {};
                    const displayedEmail = profile.fromEmail || smtpSettings.fromEmail || 'email@domaine.fr';
                    return (
                      <Box key={key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'grid', gap: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{title}</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                          <TextField label="Nom et prénom" value={profile.signatureName || ''} onChange={(event) => updateSmtpProfileSetting(key, 'signatureName', event.target.value)} disabled={loading} fullWidth />
                          <TextField label="Fonction / service" value={profile.signatureRole || ''} onChange={(event) => updateSmtpProfileSetting(key, 'signatureRole', event.target.value)} disabled={loading} fullWidth />
                          <TextField label="Téléphone" value={profile.signaturePhone || ''} onChange={(event) => updateSmtpProfileSetting(key, 'signaturePhone', event.target.value)} disabled={loading} fullWidth />
                          <TextField label="Portable" value={profile.signatureMobile || ''} onChange={(event) => updateSmtpProfileSetting(key, 'signatureMobile', event.target.value)} disabled={loading} fullWidth />
                        </Box>
                        <TextField label="Adresse" value={profile.signatureAddress || ''} onChange={(event) => updateSmtpProfileSetting(key, 'signatureAddress', event.target.value)} disabled={loading} fullWidth />
                        <Box sx={{ bgcolor: '#fff', color: '#1f2937', borderRadius: 1, p: 2, overflow: 'hidden' }}>
                          <Typography sx={{ color: '#1f5d9f', fontSize: 14, mb: 1 }}>{profile.signature || 'Bien cordialement,'}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box component="img" src="/opticdevlogo-transparent.png" alt="Optic Développement" sx={{ width: 150, maxHeight: 74, objectFit: 'contain' }} />
                            <Box sx={{ borderLeft: '4px solid #1e63d5', pl: 1.5, fontSize: 12, lineHeight: 1.4, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{profile.signatureName || 'Nom Prénom'}</Typography>
                              <Typography sx={{ fontSize: 12 }}>{profile.signatureRole || 'Fonction / service'}</Typography>
                              {profile.signaturePhone && <Typography sx={{ fontSize: 12 }}>Tél. {profile.signaturePhone}</Typography>}
                              {profile.signatureMobile && <Typography sx={{ fontSize: 12 }}>Port. {profile.signatureMobile}</Typography>}
                              <Typography sx={{ color: '#1e63d5', fontSize: 12 }}>Mail. {displayedEmail}</Typography>
                              {profile.signatureAddress && <Typography sx={{ fontSize: 12 }}>Adresse : {profile.signatureAddress}</Typography>}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                <Alert severity="info" variant="outlined">
                  Ces paramètres seront utilisés pour les mails automatiques, notamment les demandes de réapprovisionnement lorsque le stock passe sous le seuil défini.
                </Alert>

                <Divider />

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Envoyer un email de test
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilise la configuration SMTP enregistrée pour vérifier la réception et l'expéditeur.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr) auto' }, gap: 1.5, alignItems: 'start' }}>
                    <TextField
                      label="Destinataire du test"
                      type="email"
                      value={smtpTestEmail}
                      onChange={(event) => {
                        setSmtpTestEmail(event.target.value);
                        setSmtpTestMessage('');
                        setSmtpTestError('');
                      }}
                      placeholder="vous@domaine.fr"
                      disabled={loading || smtpTestSending}
                      fullWidth
                    />
                    <TextField
                      label="Expéditeur utilisé"
                      value={smtpSettings.fromEmail ? `${smtpSettings.fromName || 'Optibox'} <${smtpSettings.fromEmail}>` : 'À définir dans la configuration SMTP'}
                      InputProps={{ readOnly: true }}
                      disabled={loading}
                      fullWidth
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={<SendRoundedIcon />}
                      onClick={sendSmtpTestEmail}
                      disabled={loading || smtpTestSending || !smtpTestEmail.trim()}
                      sx={{ minHeight: 40, whiteSpace: 'nowrap' }}
                    >
                      {smtpTestSending ? 'Envoi…' : 'Envoyer le test'}
                    </Button>
                  </Box>
                  {smtpTestMessage && <Alert severity="success">{smtpTestMessage}</Alert>}
                  {smtpTestError && <Alert severity="error">{smtpTestError}</Alert>}
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => setSmtpSettings(defaultSmtpSettings)}
                    disabled={saving || loading}
                  >
                    Réinitialiser SMTP
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveSection('smtp')}
                    disabled={saving || loading}
                    sx={{ fontWeight: 900 }}
                  >
                    Enregistrer SMTP
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsRoundedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Sites
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Liste des types de magasin proposés dans la fiche site.
                    </Typography>
                  </Box>
                </Stack>

                <EditableList
                  title="Types de magasin"
                  helperText="Ajoutez, modifiez ou retirez un type explicitement."
                  values={storeTypes}
                  onChange={setStoreTypes}
                  defaults={['OPTIQUE DE BOURBON', 'ALAIN AFFLELOU']}
                  disabled={loading}
                  minRows={4}
                />
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  onClick={() => saveSection('sites')}
                  disabled={saving || loading}
                  sx={{ alignSelf: 'flex-start', fontWeight: 900 }}
                >
                  Enregistrer Sites
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <SettingsRoundedIcon color="primary" />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Logistique
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Listes utilisées dans les menus du formulaire Logistique.
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  <EditableList title="Défauts" helperText="Liste du champ Défaut." values={savDefects} onChange={setSavDefects} defaults={defaultSavSettings.defects} disabled={loading} minRows={8} />
                  <EditableList title="Pièces à remplacer" helperText="Liste du champ Pièce à remplacer." values={savReplacementParts} onChange={setSavReplacementParts} defaults={defaultSavSettings.replacementParts} disabled={loading} minRows={8} />
                  <Alert severity="info" variant="outlined">
                    Les fournisseurs et leurs emails sont gérés dans Administration &gt; Fournisseurs. Les numéros de compte sont gérés dans Administration &gt; Comptes fournisseurs.
                  </Alert>
                  <SupplierAutocompleteList
                    title="Fournisseurs autorisés — monture complète/nue"
                    helperText="Exceptions autorisées sous Garantie fournisseur. Sélectionnez uniquement un fournisseur existant."
                    values={completeFrameAllowedSuppliers}
                    options={savSuppliers}
                    onChange={setCompleteFrameAllowedSuppliers}
                    defaults={defaultSavSettings.completeFrameAllowedSuppliers}
                    disabled={loading}
                    minRows={3}
                  />
                  <EditableList title="Marques" helperText="Liste du champ Marque." values={savBrands} onChange={setSavBrands} defaults={defaultSavSettings.brands} disabled={loading} minRows={5} />
                  <EditableList title="Garanties" helperText="Liste du champ Garantie." values={savWarranties} onChange={setSavWarranties} defaults={defaultSavSettings.warranties} disabled={loading} minRows={5} />
                </Box>

                <SupplierBrandTransfer
                  suppliers={savSuppliers}
                  brands={savBrands}
                  rules={supplierBrandRules}
                  onChange={setSupplierBrandRules}
                  disabled={loading || saving}
                />

                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Pièces jointes obligatoires par fournisseur
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cochez les photos exigées. Aucune case cochée = au moins une pièce jointe standard.
                    </Typography>
                  </Box>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                    {uniqueList(savSuppliers).map((supplier) => {
                      const rule = getSupplierAttachmentRule(supplier);
                      return (
                        <Box
                          key={supplier}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) repeat(6, minmax(165px, 210px))' },
                            gap: 1,
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            {supplier}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rule.fullFramePhoto}
                                onChange={(event) => updateSupplierAttachmentRule(supplier, 'fullFramePhoto', event.target.checked)}
                                disabled={loading}
                              />
                            }
                            label="Photo monture complète"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rule.defectivePartPhoto}
                                onChange={(event) => updateSupplierAttachmentRule(supplier, 'defectivePartPhoto', event.target.checked)}
                                disabled={loading}
                              />
                            }
                            label="Photo pièce défectueuse"
                          />
                          <FormControlLabel control={<Checkbox checked={rule.serialNumberRequired} onChange={(event) => updateSupplierAttachmentRule(supplier, 'serialNumberRequired', event.target.checked)} disabled={loading} />} label="N° série obligatoire" />
                          <FormControlLabel control={<Checkbox checked={rule.mandatoryForm} onChange={(event) => updateSupplierAttachmentRule(supplier, 'mandatoryForm', event.target.checked)} disabled={loading} />} label="Formulaire obligatoire" />
                          <FormControlLabel control={<Checkbox checked={rule.purchaseInvoice} onChange={(event) => updateSupplierAttachmentRule(supplier, 'purchaseInvoice', event.target.checked)} disabled={loading} />} label="Facture d’achat" />
                          <FormControlLabel control={<Checkbox checked={rule.defectVideoRequired} onChange={(event) => updateSupplierAttachmentRule(supplier, 'defectVideoRequired', event.target.checked)} disabled={loading} />} label="Vidéo du défaut" />
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Pièces jointes obligatoires par marque
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ces règles s'ajoutent aux règles fournisseur lorsqu'une marque exige des documents spécifiques.
                    </Typography>
                  </Box>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                    {uniqueList(savBrands).map((brand) => {
                      const rule = getBrandAttachmentRule(brand);
                      return (
                        <Box
                          key={brand}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) repeat(6, minmax(165px, 210px))' },
                            gap: 1,
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            {brand}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rule.fullFramePhoto}
                                onChange={(event) => updateBrandAttachmentRule(brand, 'fullFramePhoto', event.target.checked)}
                                disabled={loading}
                              />
                            }
                            label="Photo monture complète"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rule.defectivePartPhoto}
                                onChange={(event) => updateBrandAttachmentRule(brand, 'defectivePartPhoto', event.target.checked)}
                                disabled={loading}
                              />
                            }
                            label="Photo pièce défectueuse"
                          />
                          <FormControlLabel control={<Checkbox checked={rule.serialNumberRequired} onChange={(event) => updateBrandAttachmentRule(brand, 'serialNumberRequired', event.target.checked)} disabled={loading} />} label="N° série obligatoire" />
                          <FormControlLabel control={<Checkbox checked={rule.mandatoryForm} onChange={(event) => updateBrandAttachmentRule(brand, 'mandatoryForm', event.target.checked)} disabled={loading} />} label="Formulaire obligatoire" />
                          <FormControlLabel control={<Checkbox checked={rule.purchaseInvoice} onChange={(event) => updateBrandAttachmentRule(brand, 'purchaseInvoice', event.target.checked)} disabled={loading} />} label="Facture d’achat" />
                          <FormControlLabel control={<Checkbox checked={rule.defectVideoRequired} onChange={(event) => updateBrandAttachmentRule(brand, 'defectVideoRequired', event.target.checked)} disabled={loading} />} label="Vidéo du défaut" />
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => {
                      setSavDefects(defaultSavSettings.defects);
                      setSavReplacementParts(defaultSavSettings.replacementParts);
                      setSupplierAttachmentRules(defaultSavSettings.supplierAttachmentRules);
                      setBrandAttachmentRules(defaultSavSettings.brandAttachmentRules);
                      setSupplierBrandRules(defaultSavSettings.supplierBrandRules);
                      setSavBrands(defaultSavSettings.brands);
                      setSavWarranties(defaultSavSettings.warranties);
                      setCompleteFrameAllowedSuppliers(defaultSavSettings.completeFrameAllowedSuppliers);
                    }}
                    disabled={saving || loading}
                  >
                    Réinitialiser Logistique
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveSection('sav')}
                    disabled={saving || loading}
                    sx={{ fontWeight: 900 }}
                  >
                    Enregistrer Logistique
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <SettingsRoundedIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        Menu
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Personnalise les sections, libellés, icônes, ordre et visibilité des onglets.
                      </Typography>
                    </Box>
                  </Stack>
                  <Button type="button" variant="outlined" onClick={addMenuGroup} disabled={loading}>
                    Ajouter une section
                  </Button>
                </Stack>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                    Sections
                  </Typography>
                  <Stack spacing={1.5}>
                    {displayedGroups.map((group) => (
                      <Box
                        key={group.id}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (dragging?.type === 'group') moveGroup(dragging.id, group.id);
                          if (dragging?.type === 'item') moveItem(dragging.id, null, group.id);
                          handleDragEnd();
                        }}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: '42px minmax(180px, 1fr) minmax(160px, 220px) 110px',
                          },
                          gap: 1.5,
                          alignItems: 'center',
                          opacity: dragging?.id === group.id ? 0.55 : 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          draggable
                          onDragStart={handleDragStart('group', group.id)}
                          onDragEnd={handleDragEnd}
                          disabled={loading}
                          sx={{ cursor: loading ? 'default' : 'grab' }}
                        >
                          <DragIndicatorRoundedIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          label="Nom du dossier"
                          value={group.label}
                          onChange={(event) => handleGroupChange(group.id, 'label', event.target.value)}
                          disabled={loading}
                        />
                        <TextField
                          size="small"
                          label="Icône"
                          select
                          value={group.icon}
                          onChange={(event) => handleGroupChange(group.id, 'icon', event.target.value)}
                          disabled={loading}
                        >
                          {MENU_ICON_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          label="Ordre"
                          type="number"
                          value={group.order}
                          onChange={(event) => handleGroupChange(group.id, 'order', event.target.value)}
                          disabled={loading}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                    Onglets
                  </Typography>
                  <Stack spacing={1.5}>
                    {displayedItems.map((item) => (
                      <Box
                        key={item.id}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (dragging?.type === 'item') moveItem(dragging.id, item.id, item.groupId);
                          handleDragEnd();
                        }}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            md: '42px minmax(180px, 1.2fr) minmax(150px, 180px) minmax(180px, 220px) 100px 120px',
                          },
                          gap: 1.5,
                          alignItems: 'center',
                          opacity: dragging?.id === item.id ? 0.55 : 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          draggable
                          onDragStart={handleDragStart('item', item.id)}
                          onDragEnd={handleDragEnd}
                          disabled={loading}
                          sx={{ cursor: loading ? 'default' : 'grab' }}
                        >
                          <DragIndicatorRoundedIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          label="Nom de l'onglet"
                          value={item.label}
                          onChange={(event) => handleItemChange(item.id, 'label', event.target.value)}
                          disabled={loading}
                        />
                        <TextField
                          size="small"
                          label="Icône"
                          select
                          value={item.icon}
                          onChange={(event) => handleItemChange(item.id, 'icon', event.target.value)}
                          disabled={loading}
                        >
                          {MENU_ICON_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          label="Section"
                          select
                          value={item.groupId}
                          onChange={(event) => handleItemChange(item.id, 'groupId', event.target.value)}
                          disabled={loading}
                        >
                          {menuConfig.groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          label="Ordre"
                          type="number"
                          value={item.order}
                          onChange={(event) => handleItemChange(item.id, 'order', event.target.value)}
                          disabled={loading}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={item.enabled !== false}
                              onChange={(event) => handleItemChange(item.id, 'enabled', event.target.checked)}
                              disabled={loading || item.id === 'settings'}
                            />
                          }
                          label="Afficher"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => setMenuConfig(normalizeMenuConfig(DEFAULT_MENU_CONFIG))}
                    disabled={saving || loading}
                  >
                    Réinitialiser Menu
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveSection('menu')}
                    disabled={saving || loading}
                    sx={{ fontWeight: 900 }}
                  >
                    Enregistrer Menu
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Stack>
  );
}
